
import React, { forwardRef, useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { StyleSheet } from 'react-native';

import { Text, View } from '../../../../../components/Common/react-native-components';
import ExpandableGrid from '../../../../../components/ExpandableGrid/ExapandableGridWrapper';
import MultiFeatureCard from '../../../../../components/Common/Card/MultiOptionsCard/MultiFeatureCard';
import LegendBar from '../LegendBar';

import useExpandableGridHelper from '../../../hooks/useExpandableGridHelper';
import SentimentProgressBar from './SentimentProgressBar';
import EmojiMap from '../../../../../components/Common/EmojiMap';

const SentimentDistributionByTopic = forwardRef((props, ref) => {
  const { itemData, timeperiodLabel, applied_filter_values, widgetId, reduxData } = props;

  const [activeSwitch, setActiveSwitch] = useState();
  const [selectedMetricType, setSelectedMetricType] = useState([get(itemData, 'defaultMetricType', "defaultMetricType")]);
  const [displayMetricType, setDisplayMetricType] = useState("defaultMetricType");

  const dispatch = useDispatch();
  const tableData = get(reduxData, 'tableData', {});

  const handleAggregationResponse = useCallback(
    ({ rawData, tableData, widget_config }) => {
      const topicLevel =
        widget_config.topic_filter && !isEmpty(widget_config.topic_filter)
          ? widget_config.topic_filter
          : { root: 'All Topics' };

      dispatch({
        type: 'SAVE_WIDGET_CHART_DATA',
        payload: {
          widgetId,
          metricType: widget_config.metric_type,
          chartData: rawData,
          currentLevel: JSON.stringify(topicLevel)
        }
      });
      if (widget_config?.changeLevel) {
        dispatch({
          type: 'SET_WIDGET_CURRENT_LEVEL',
          payload: { widgetId, currentLevel: topicLevel }
        });
      }
    },
    [dispatch, widgetId]
  );
  const { fetchData, loadingData, tableColumns, setTableColumns, error } = useExpandableGridHelper({
    widgetId,
    appliedFilterValues: applied_filter_values,
    onAggregationResponseFn: handleAggregationResponse
  });

  const handleMetricChange = async selectedOption => {
    const nextMetric = "defaultMetricType"
    setSelectedMetricType(selectedOption);

    const result = await fetchData({
      widget_config: { topic_filter: {}, topic_depth: 'l1', metric_type: nextMetric }
    });

    dispatch({
      type: 'SAVE_WIDGET_TABLE_DATA',
      payload: {
        widgetId,
        metricType: nextMetric,
        tableData: result?.tableData,
        // tableColumns: newColumns,
        cacheKey: 'none-none-1'
      }
    });

    if (result) setDisplayMetricType(nextMetric);
  };

  const fetchNestedData = row => {
    const currentDepth = get(row, 'topic_depth', 'l1');
    const nextDepth = currentDepth === 'l1' ? 'l2' : 'l3';
    const topic_filter = get(row, 'topic_filter', {});
    const topicName = get(row, 'primary', '');

    const widget_config = {
      topic_depth: nextDepth,
      topic_filter: { ...topic_filter, [currentDepth]: topicName },
      metric_type: displayMetricType,
    };
    return fetchData({ widget_config });
  };


  useEffect(() => {
    handleMetricChange(selectedMetricType);
  }, []);

  useEffect(() => {
    dispatch({
      type: 'SET_WIDGET_CURRENT_LEVEL',
      payload: { widgetId, currentLevel: { root: 'All Topics' } }
    });
  }, []);


  const renderContent = () => {
    return (
      <ExpandableGrid
        fetchData={opt =>
          fetchData({ ...opt, widget_config: { ...opt.widget_config, metric_type: displayMetricType } })
        }
        // loading={false}
        componentMap={{
          bar: {
            body: props => {
              return <SentimentProgressBar chartDefaultConfig={get(itemData, "sentimentChartDefaultConfig", {})} {...props} />;
            }
          },
          perc_sentiment_negative: {
            header: props => {
              return (
                <View
                  style={{
                    borderColor: 'rgb(221, 225, 229)',
                    borderBottomWidth: 1,
                    justifyContent: 'center',
                    width: '100%',
                    flex: 1,
                    paddingLeft: 8
                  }}>
                  <EmojiMap type='SADSENTIMENT' />
                </View>
              );
            }
          },
          perc_sentiment_positive: {
            header: props => {
              return (
                <View
                  style={{
                    borderColor: 'rgb(221, 225, 229)',
                    borderBottomWidth: 1,
                    justifyContent: 'center',
                    width: '100%',
                    paddingLeft: 8,
                    flex: 1
                  }}>
                  <EmojiMap type='HAPPYSENTIMENT' />
                </View>
              );
            }
          },
          perc_sentiment_neutral: {
            header: props => {
              return (
                <View
                  style={{
                    borderColor: 'rgb(221, 225, 229)',
                    borderBottomWidth: 1,
                    justifyContent: 'center',
                    width: '100%',
                    paddingLeft: 8,
                    flex: 1
                  }}>
                  <EmojiMap type='NEUTRALSENTIMENT' />
                </View>
              );
            }
          }
        }}
        getNestedData={fetchNestedData}
        columns={itemData?.columns || []}
        widgetSpecificKeys={{ widgetId }}
        metricKey={displayMetricType}
        dataCache={tableData || {}}
      />
    )
  };


  return (
    <MultiFeatureCard
      title={get(itemData, 'title')}
      subTitle={get(itemData, 'subTitle') + ' ' + timeperiodLabel}
      enableSwitchTab={itemData?.enableSwitchTab}
      switchOptions={itemData?.switchOptions}
      style={{ childContainer: { minHeight: 250, paddingBottom: 15 } }}
      loading={loadingData}
      isNoData={error}
      renderContent={sw => {
        setActiveSwitch(sw);
        return (
          <View>
            {error ? <Text style={styles.errorText}>{error}</Text> : renderContent()}
          </View>
        );
      }}
      renderFooter={() =>
        <LegendBar items={itemData?.legend} />
      }
    />
  );
});

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    backgroundColor: 'rgba(255,255,255,0.6)',
    alignItems: 'center',
    justifyContent: 'center'
  },
  label: { marginBottom: 8, fontWeight: '400', color: '#6b7589' },
  errorText: { color: '#d9534f', textAlign: 'center', marginTop: 20 }
});

export default SentimentDistributionByTopic;
